<template>
  <div>
    <div class="nav d-flex justify-content-between align-items-center">
      <span v-if="filteredUsers.length > perPage" class="ml-5 mr-auto">
        Displaying
        <strong class="text-primary">
          {{ (pageIndex - 1) * perPage + 1 }} &ndash;
          {{ pageIndex * perPage - (perPage - currentPage.length) }}
        </strong>
        of <strong class="text-primary">{{ filteredUsers.length }}</strong>
      </span>
      <Pagination
        v-if="filteredUsers.length > perPage"
        v-model="pageIndex"
        :total="totalPages"
        :per-page="perPage"
      />
      <input
        v-if="filteredUsers.length > perPage || filter.length"
        v-model="filter"
        type="text"
        class="form-control ml-auto mr-3 mb-3"
        style="max-width: 15%;"
        placeholder="Search"
        aria-label="Search"
      />
    </div>

    <table class="table table-hover table-responsive-lg border-bottom">
      <thead>
        <tr>
          <th>
            <RolesFilter
              v-model="filteredRoles"
              :allowed-roles="allowedRoles"
            />
            Name
          </th>
          <th>Email</th>
          <th>Department</th>
          <th>FERPA</th>
          <th>Status</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="user in currentPage" :key="user.emplId">
          <td>
            <div class="text-nowrap d-flex align-items-center">
              <RolesDropdown
                :user="user"
                :allowed-roles="allowedRoles"
                @save-roles="saveRoles"
              ></RolesDropdown>
              <div class="d-flex flex-column">
                <strong>{{ user.cn }}</strong>
                <div>
                  <span
                    v-for="role in user.roles"
                    :key="role"
                    class="badge badge-primary mr-1"
                    >{{ role }}</span
                  >
                </div>
              </div>
            </div>
          </td>

          <td>
            <div class="overflow-auto text-nowrap">{{ user.mail }}</div>
          </td>
          <td>
            <div class="overflow-auto text-nowrap">
              {{ user.employeePrimaryDeptName }}
              {{ user.employeePrimaryDept && `(${user.employeePrimaryDept})` }}
            </div>
          </td>
          <td class="pl-3">
            <strong>
              {{ user.employeeIsFerpaTrained || "N" }}
            </strong>
          </td>
          <td class="pl-3">
            <strong>
              {{ getUserStatus(user) }}
            </strong>
          </td>
          <td>
            <button
              class="delete btn btn-outline-light btn-sm px-2 py-1"
              @click="deleteUser(user)"
            >
              &times;
            </button>
          </td>
        </tr>
        <div v-if="!users">No users</div>
      </tbody>
    </table>
    <div class="px-2">
      <AddUserModal
        class="mx-1"
        :adding="addingUser"
        @input="$emit('add-user', $event)"
      />
    </div>
  </div>
</template>

<script>
import RolesDropdown from "./RolesDropdown.vue"
import RolesFilter from "./RolesFilter.vue"
import Pagination from "./Pagination.vue"
import AddUserModal from "./AddUserModal.vue"
export default {
  components: {
    AddUserModal,
    RolesDropdown,
    RolesFilter,
    Pagination,
  },
  props: {
    users: { required: true, type: Array },
    allowedRoles: { required: true, type: Array },
    addingUser: { required: true, type: Boolean },
  },
  data() {
    return {
      perPage: 10,
      pageIndex: 1,
      filteredRoles: [],
      filter: "",
    }
  },
  computed: {
    filteredUsers() {
      if (!this.filter && !this.filteredRoles) return this.users
      return this.users
        .filter((user) => this.matchSearch(user) && this.matchRoles(user))
        .sort((a, b) => a.cn.localeCompare(b.cn))
    },
    currentPage() {
      const start = (this.pageIndex - 1) * this.perPage
      const end = start + this.perPage
      return this.filteredUsers.slice(start, end)
    },
    totalPages() {
      return Math.ceil(this.filteredUsers.length / this.perPage)
    },
  },
  watch: {
    filter() {
      this.pageIndex = 1
    },
  },
  beforeMount() {
    this.filteredRoles = this.allowedRoles
  },
  methods: {
    async saveRoles(user) {
      const oldRoles = this.users.find((u) => u.emplId == user.emplId).roles
      const newRolesNotUser = user.roles.filter(
        (r) => !oldRoles.includes(r) && r !== "user",
      )
      // Should use previous delete method if we are taking all roles.
      if (!user.roles.length) {
        this.deleteUser(user)
        return
      }
      if (!newRolesNotUser.length) {
        this.$emit("update-user", user)
      } else {
        const confirm = await this.$alert.fire({
          title: "Are you sure?",
          icon: "question",
          showCancelButton: true,
          confirmButtonText: "Yes",
          html: `
            <div class="d-flex flex-column align-items-center">
              <p class="w-75">
                Clicking "Yes" will update user
                <strong>
                  ${user.cn}
                </strong>
                with the following user roles:
              </p>
              <ul>
                <li>${Array.from(newRolesNotUser).join("</li><li>")}</li>
              </ul>
            </div>
          `,
        })
        if (confirm.value) {
          this.$emit("update-user", user)
        }
      }
    },
    async deleteUser(user) {
      const confirm = await this.$alert.fire({
        title: "Delete user?",
      })
      if (confirm.value) {
        this.$emit("delete-user", user)
      }
    },
    getUserStatus(user) {
      const {
        eduPersonPrimaryAffiliation,
        dccPrimaryStatus,
        employeeStatus,
      } = user
      if (eduPersonPrimaryAffiliation === "dcc") return dccPrimaryStatus || "-"
      return employeeStatus || "-"
    },
    matchSearch(user) {
      return (
        user.cn.toLowerCase().includes(this.filter) ||
        user.mail.toLowerCase().includes(this.filter) ||
        user.employeePrimaryDeptName.toLowerCase().includes(this.filter) ||
        user.employeePrimaryDept.includes(this.filter)
      )
    },
    matchRoles(user) {
      return user.roles.filter((r) => this.filteredRoles.includes(r)).length
    },
  },
}
</script>

<style lang="postcss">
.table td {
  padding: 0.5rem;
  vertical-align: middle !important;
}
.delete {
  font-weight: 700;
  font-size: 1.5rem;
  line-height: 1;
  color: #555;
}
</style>
